/**
 * Shared markdown diff utilities — block-level approach.
 *
 * Parses markdown into semantic blocks (paragraph, table, heading, code block,
 * list, blockquote, image, hr) and diffs at the block level. When a block is
 * modified, word-level highlighting is applied within it.
 *
 * This avoids the fragmented line-level diffs that break table rendering and
 * cause yellow background bleed.
 */

export interface DiffSegment {
  type: "context" | "added" | "removed";
  lines: string[];
}

interface DiffLine {
  type: "context" | "added" | "removed";
  text: string;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Block-level markdown parser
 * ═══════════════════════════════════════════════════════════════════════════ */

type BlockType = "heading" | "table" | "code" | "list" | "blockquote" | "hr" | "image" | "html" | "paragraph" | "blank";

interface MdBlock {
  type: BlockType;
  lines: string[];
  /** Normalized text for comparison (trimmed, whitespace-collapsed) */
  norm: string;
}

const HEADING_RE = /^#{1,6}\s/;
const TABLE_LINE_RE = /^\s*\|/;
const CODE_FENCE_RE = /^(`{3,}|~{3,})/;
const LIST_RE = /^(\s*)([-*+]|\d+[.)]) /;
const BLOCKQUOTE_RE = /^>\s?/;
const HR_RE = /^([-*_])\s*(\1\s*){2,}$/;
const IMAGE_LINE_RE = /^\s*!\[.*?\]\(.*?\)\s*$/;
const HTML_BLOCK_RE = /^\s*<(?:div|section|details|summary|aside|nav|figure|figcaption|blockquote|table|thead|tbody|tr|td|th|dl|dt|dd|pre|p)\b/i;

/** Normalize lines for comparison.
 *  - Strips trailing whitespace and \r
 *  - Normalizes table separator rows
 *  - Un-escapes TipTap's backslash-escaped markdown chars (\*\* → **, \_ → _, etc.)
 *    so formatting-only re-serialization differences don't count as changes.
 */
function normForCompare(lines: string[]): string {
  const cleaned = lines.map(l => {
    let r = l.replace(/\r$/, "").trimEnd();
    // Normalize table separator rows
    if (/^\s*\|[\s-:|]+\|\s*$/.test(r)) {
      r = r.replace(/[^|:-]/g, "");
    }
    // Un-escape TipTap backslash-escaped markdown characters.
    // TipTap serializes e.g. closing bold as \*\* instead of **.
    // Also handles \_, \~, \`, \[, \], \#, \>, \+, \-, \., \!
    r = r.replace(/\\([*_~`\[\]#>+\-.!\\])/g, "$1");
    return r;
  });

  // Normalize list-gap: TipTap always outputs a blank line before list blocks.
  // Insert blank line before list items that follow a non-blank paragraph line.
  const listItemRe = /^(\s*)(?:[-*+]|\d+\.)\s/;
  const structuralRe = /^(?:#|>|\||```|~~~|---|[*]{3}|___)/;
  const result: string[] = [cleaned[0]];
  for (let i = 1; i < cleaned.length; i++) {
    const curr = cleaned[i];
    const prev = cleaned[i - 1];
    if (
      listItemRe.test(curr) &&
      prev.trim() !== "" &&
      !listItemRe.test(prev) &&
      !structuralRe.test(prev.trim())
    ) {
      result.push("");
    }
    result.push(curr);
  }

  return result.join("\n");
}

/**
 * Parse markdown text into semantic blocks.
 * Each block is a self-contained unit: a heading, a full table, a code block,
 * a list, a blockquote, a paragraph, etc.
 */
function parseBlocks(text: string): MdBlock[] {
  const lines = text.split("\n");
  const blocks: MdBlock[] = [];
  let i = 0;

  function pushBlock(type: BlockType, blockLines: string[]) {
    blocks.push({ type, lines: blockLines, norm: normForCompare(blockLines) });
  }

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trimEnd();

    // Blank lines
    if (trimmed === "") {
      // Collect consecutive blank lines as one blank block
      const start = i;
      while (i < lines.length && lines[i].trimEnd() === "") i++;
      pushBlock("blank", lines.slice(start, i));
      continue;
    }

    // Fenced code block
    const codeFence = trimmed.match(CODE_FENCE_RE);
    if (codeFence) {
      const fence = codeFence[1];
      const fenceChar = fence[0];
      const fenceLen = fence.length;
      const start = i;
      i++;
      while (i < lines.length) {
        const cl = lines[i].trimEnd();
        // Closing fence: same char, at least same length, nothing else
        if (cl.length >= fenceLen && cl.split("").every(c => c === fenceChar) && cl.length >= fenceLen) {
          // More precise: must be only fence chars
          const stripped = cl.replace(new RegExp(`\\${fenceChar}`, "g"), "");
          if (stripped.trim() === "") { i++; break; }
        }
        i++;
      }
      pushBlock("code", lines.slice(start, i));
      continue;
    }

    // Heading
    if (HEADING_RE.test(trimmed)) {
      pushBlock("heading", [lines[i]]);
      i++;
      continue;
    }

    // HR
    if (HR_RE.test(trimmed)) {
      pushBlock("hr", [lines[i]]);
      i++;
      continue;
    }

    // Image (standalone line)
    if (IMAGE_LINE_RE.test(trimmed) || /^\s*<img\s/i.test(trimmed)) {
      pushBlock("image", [lines[i]]);
      i++;
      continue;
    }

    // Table: starts with | and has at least a separator row nearby
    if (TABLE_LINE_RE.test(trimmed)) {
      const start = i;
      while (i < lines.length && TABLE_LINE_RE.test(lines[i].trimEnd())) i++;
      pushBlock("table", lines.slice(start, i));
      continue;
    }

    // Blockquote
    if (BLOCKQUOTE_RE.test(trimmed)) {
      const start = i;
      while (i < lines.length) {
        const bl = lines[i].trimEnd();
        if (bl === "" || BLOCKQUOTE_RE.test(bl)) {
          // Include blank lines within blockquote if followed by more blockquote
          if (bl === "") {
            if (i + 1 < lines.length && BLOCKQUOTE_RE.test(lines[i + 1].trimEnd())) {
              i++;
              continue;
            }
            break;
          }
          i++;
        } else {
          break;
        }
      }
      pushBlock("blockquote", lines.slice(start, i));
      continue;
    }

    // List (unordered or ordered)
    if (LIST_RE.test(trimmed)) {
      const start = i;
      i++;
      while (i < lines.length) {
        const ll = lines[i];
        const lt = ll.trimEnd();
        // Continue list: another list item, or indented continuation, or blank line followed by list item
        if (LIST_RE.test(lt)) { i++; continue; }
        if (lt === "") {
          // Blank line — check if next line continues the list
          if (i + 1 < lines.length && (LIST_RE.test(lines[i + 1].trimEnd()) || /^\s{2,}/.test(lines[i + 1]))) {
            i++;
            continue;
          }
          break;
        }
        if (/^\s{2,}/.test(ll)) { i++; continue; } // indented continuation
        break;
      }
      pushBlock("list", lines.slice(start, i));
      continue;
    }

    // HTML block
    if (HTML_BLOCK_RE.test(trimmed)) {
      const start = i;
      i++;
      // Consume until blank line or end
      while (i < lines.length && lines[i].trimEnd() !== "") i++;
      pushBlock("html", lines.slice(start, i));
      continue;
    }

    // Paragraph: everything else until blank line or a new block type
    {
      const start = i;
      i++;
      while (i < lines.length) {
        const pl = lines[i].trimEnd();
        if (pl === "") break;
        if (HEADING_RE.test(pl)) break;
        if (CODE_FENCE_RE.test(pl)) break;
        if (HR_RE.test(pl)) break;
        if (TABLE_LINE_RE.test(pl)) break;
        if (BLOCKQUOTE_RE.test(pl)) break;
        if (LIST_RE.test(pl)) break;
        if (IMAGE_LINE_RE.test(pl) || /^\s*<img\s/i.test(pl)) break;
        if (HTML_BLOCK_RE.test(pl)) break;
        i++;
      }
      pushBlock("paragraph", lines.slice(start, i));
    }
  }

  return blocks;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Line-level diff (kept for ContentDiff source view and DiffSummaryBadge)
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Compute a line-level diff between two markdown strings.
 */
export function computeMarkdownDiff(
  beforeText: string,
  afterText: string,
): DiffSegment[] {
  const oldLines = beforeText.split("\n");
  const newLines = afterText.split("\n");

  const normalize = (line: string): string => {
    let l = line.replace(/\r$/, "").trimEnd();
    if (/^\s*\|[\s-:|]+\|\s*$/.test(l)) {
      l = l.replace(/\s+/g, "").replace(/\|/g, " | ").trim().replace(/^ \| /, "|").replace(/ \| $/, "|");
      l = l.replace(/[^|:-]/g, "");
    }
    // Un-escape TipTap backslash-escaped markdown characters
    l = l.replace(/\\([*_~`\[\]#>+\-.!\\])/g, "$1");
    return l;
  };

  const oldNorm = oldLines.map(normalize);
  const newNorm = newLines.map(normalize);
  const m = oldNorm.length;
  const n = newNorm.length;

  const diffLines: DiffLine[] =
    m * n > 500_000
      ? sequentialDiffNorm(oldLines, newLines, oldNorm, newNorm)
      : lcsDiffNorm(oldLines, newLines, oldNorm, newNorm);

  return groupIntoSegments(diffLines);
}

/**
 * Count the number of changed blocks between two markdown strings.
 * Uses the same block-level LCS as the rendered diff view.
 * Returns the count of non-context, non-blank blocks (added + removed + modified).
 */
export function countChangedBlocks(
  beforeText: string,
  afterText: string,
): number {
  if (!beforeText && !afterText) return 0;
  if (!beforeText) return parseBlocks(afterText).filter(b => b.type !== "blank").length;
  if (!afterText) return parseBlocks(beforeText).filter(b => b.type !== "blank").length;

  const oldBlocks = parseBlocks(beforeText);
  const newBlocks = parseBlocks(afterText);

  const m = oldBlocks.length, n = newBlocks.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldBlocks[i - 1].norm === newBlocks[j - 1].norm
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  // Collect raw diff ops: 'del' for removed blocks, 'add' for inserted blocks
  const ops: Array<"del" | "add"> = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldBlocks[i - 1].norm === newBlocks[j - 1].norm) {
      i--; j--; // context — skip
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      if (newBlocks[j - 1].type !== "blank") ops.push("add");
      j--;
    } else {
      if (oldBlocks[i - 1].type !== "blank") ops.push("del");
      i--;
    }
  }
  ops.reverse();

  // Collapse adjacent del+add (or add+del) pairs into a single "modified" count
  let count = 0;
  let k = 0;
  while (k < ops.length) {
    if (k + 1 < ops.length && ops[k] !== ops[k + 1]) {
      // del+add or add+del = one modification
      count++;
      k += 2;
    } else {
      count++;
      k++;
    }
  }
  return count;
}

function lcsDiffNorm(oldLines: string[], newLines: string[], oldNorm: string[], newNorm: string[]): DiffLine[] {
  const m = oldNorm.length;
  const n = newNorm.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldNorm[i - 1] === newNorm[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const stack: DiffLine[] = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldNorm[i - 1] === newNorm[j - 1]) {
      stack.push({ type: "context", text: oldLines[i - 1] }); i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      stack.push({ type: "added", text: newLines[j - 1] }); j--;
    } else {
      stack.push({ type: "removed", text: oldLines[i - 1] }); i--;
    }
  }
  stack.reverse();
  return stack;
}

function sequentialDiffNorm(oldLines: string[], newLines: string[], oldNorm: string[], newNorm: string[]): DiffLine[] {
  const result: DiffLine[] = [];
  const max = Math.max(oldLines.length, newLines.length);
  for (let i = 0; i < max; i++) {
    const o = i < oldLines.length ? oldLines[i] : undefined;
    const n = i < newLines.length ? newLines[i] : undefined;
    const oN = i < oldNorm.length ? oldNorm[i] : undefined;
    const nN = i < newNorm.length ? newNorm[i] : undefined;
    if (oN === nN && o !== undefined) {
      result.push({ type: "context", text: o });
    } else {
      if (o !== undefined) result.push({ type: "removed", text: o });
      if (n !== undefined) result.push({ type: "added", text: n });
    }
  }
  return result;
}

function groupIntoSegments(lines: DiffLine[]): DiffSegment[] {
  if (lines.length === 0) return [];
  const segments: DiffSegment[] = [];
  let current: DiffSegment = { type: lines[0].type, lines: [lines[0].text] };
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].type === current.type) {
      current.lines.push(lines[i].text);
    } else {
      segments.push(current);
      current = { type: lines[i].type, lines: [lines[i].text] };
    }
  }
  segments.push(current);
  return segments;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Block-level diff → RenderSegments (for RenderedDiffView)
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface RenderSegment {
  type: "context" | "added" | "removed" | "modified";
  markdown: string;
}

/**
 * Build renderable segments by diffing at the BLOCK level.
 *
 * 1. Parse both sides into semantic blocks
 * 2. LCS-diff the blocks (comparing normalized content)
 * 3. Paired modified blocks get word-level highlights
 * 4. Each segment maps to exactly one semantic block (or group of blocks)
 */
export function buildRenderSegments(
  segments: DiffSegment[],
  side: "before" | "after",
): RenderSegment[] {
  // Reconstruct the before/after text from the line-level segments
  const beforeLines: string[] = [];
  const afterLines: string[] = [];
  for (const seg of segments) {
    if (seg.type === "context") {
      beforeLines.push(...seg.lines);
      afterLines.push(...seg.lines);
    } else if (seg.type === "removed") {
      beforeLines.push(...seg.lines);
    } else {
      afterLines.push(...seg.lines);
    }
  }

  const beforeText = beforeLines.join("\n");
  const afterText = afterLines.join("\n");

  return buildBlockDiff(beforeText, afterText, side);
}

/** Core block-level diff algorithm. */
function buildBlockDiff(
  beforeText: string,
  afterText: string,
  side: "before" | "after",
): RenderSegment[] {
  const oldBlocks = parseBlocks(beforeText);
  const newBlocks = parseBlocks(afterText);

  // LCS on blocks using normalized content
  const m = oldBlocks.length, n = newBlocks.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldBlocks[i - 1].norm === newBlocks[j - 1].norm
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  // Backtrack to produce block-level diff
  interface BlockDiffEntry {
    type: "context" | "removed" | "added";
    block: MdBlock;
  }
  const stack: BlockDiffEntry[] = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldBlocks[i - 1].norm === newBlocks[j - 1].norm) {
      stack.push({ type: "context", block: side === "before" ? oldBlocks[i - 1] : newBlocks[j - 1] });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      stack.push({ type: "added", block: newBlocks[j - 1] });
      j--;
    } else {
      stack.push({ type: "removed", block: oldBlocks[i - 1] });
      i--;
    }
  }
  stack.reverse();

  // Group into segments, pairing removed→added as modifications
  const result: RenderSegment[] = [];
  let k = 0;
  while (k < stack.length) {
    const entry = stack[k];

    // Detect removed→added pairs (block modification)
    if (entry.type === "removed") {
      // Collect consecutive removed blocks
      const removedBlocks: MdBlock[] = [];
      while (k < stack.length && stack[k].type === "removed") {
        removedBlocks.push(stack[k].block);
        k++;
      }
      // Collect consecutive added blocks that follow
      const addedBlocks: MdBlock[] = [];
      while (k < stack.length && stack[k].type === "added") {
        addedBlocks.push(stack[k].block);
        k++;
      }

      if (addedBlocks.length > 0) {
        // Pair them as modifications
        const removedText = removedBlocks.map(b => b.lines.join("\n")).join("\n");
        const addedText = addedBlocks.map(b => b.lines.join("\n")).join("\n");
        const sim = similarity(removedText, addedText);
        const hasImages = removedText.includes("![") || addedText.includes("![");

        if (sim < 0.3 && !hasImages) {
          // Too different — show as separate add/remove
          if (side === "before") {
            result.push({ type: "removed", markdown: removedText });
          } else {
            result.push({ type: "added", markdown: addedText });
          }
        } else {
          // Modified — apply word-level diff within the block
          const removedLines = removedText.split("\n");
          const addedLines = addedText.split("\n");
          if (side === "before") {
            result.push({
              type: "modified",
              markdown: buildWordHighlightedLines(removedLines, addedLines, "removed"),
            });
          } else {
            result.push({
              type: "modified",
              markdown: buildWordHighlightedLines(removedLines, addedLines, "added"),
            });
          }
        }
      } else {
        // Pure removal, no paired adds
        if (side === "before") {
          const text = removedBlocks.map(b => b.lines.join("\n")).join("\n");
          result.push({ type: "removed", markdown: text });
        }
        // On "after" side, removed blocks are skipped
      }
      continue;
    }

    if (entry.type === "added") {
      // Pure addition (no preceding removed)
      if (side === "after") {
        result.push({ type: "added", markdown: entry.block.lines.join("\n") });
      }
      k++;
      continue;
    }

    // Context
    result.push({ type: "context", markdown: entry.block.lines.join("\n") });
    k++;
  }

  return result;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Word-level diff helpers
 * ═══════════════════════════════════════════════════════════════════════════ */

/** Similarity ratio (0–1) between two strings based on word-level LCS. */
function similarity(a: string, b: string): number {
  const aw = tokenize(a);
  const bw = tokenize(b);
  if (aw.length === 0 && bw.length === 0) return 1;
  const lcsLen = wordLcsLength(aw, bw);
  return (2 * lcsLen) / (aw.length + bw.length);
}

function tokenize(text: string): string[] {
  return text.match(/\S+|\s+/g) || [];
}

function wordLcsLength(a: string[], b: string[]): number {
  const m = a.length, n = b.length;
  const prev = new Array(n + 1).fill(0);
  const curr = new Array(n + 1).fill(0);
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      curr[j] = a[i - 1] === b[j - 1]
        ? prev[j - 1] + 1
        : Math.max(prev[j], curr[j - 1]);
    }
    for (let j = 0; j <= n; j++) { prev[j] = curr[j]; curr[j] = 0; }
  }
  return prev[n];
}

function wordLcsDiff(
  oldTokens: string[],
  newTokens: string[],
): { type: "equal" | "removed" | "added"; text: string }[] {
  const m = oldTokens.length, n = newTokens.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldTokens[i - 1] === newTokens[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const result: { type: "equal" | "removed" | "added"; text: string }[] = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldTokens[i - 1] === newTokens[j - 1]) {
      result.push({ type: "equal", text: oldTokens[i - 1] }); i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.push({ type: "added", text: newTokens[j - 1] }); j--;
    } else {
      result.push({ type: "removed", text: oldTokens[i - 1] }); i--;
    }
  }
  result.reverse();

  // Post-process: shift diff markers to rightmost position for cleaner output
  for (let k = 0; k < result.length; k++) {
    const tok = result[k];
    if (tok.type !== "added" && tok.type !== "removed") continue;
    for (let q = k + 1; q < result.length; q++) {
      if (result[q].type === "equal" && result[q].text === tok.text) {
        result[k] = result[q];
        result[q] = tok;
        break;
      }
      if (result[q].type !== tok.type && result[q].type !== "equal") break;
    }
  }

  return result;
}


/**
 * Build word-level highlighted markdown for a removed→added block pair.
 *
 * For table lines: diffs cell-by-cell to avoid breaking pipe syntax.
 * For regular lines: diffs word-by-word with <span> highlights.
 * Lines that are too dissimilar skip word-level diff.
 */
function buildWordHighlightedLines(
  removedLines: string[],
  addedLines: string[],
  side: "removed" | "added",
): string {
  const pairedCount = Math.min(removedLines.length, addedLines.length);
  const outputLines: string[] = [];
  const imageLineRe = /!\[.*?\]\(.*?\)|<img\s/i;
  const tableLineRe = /^\s*\|/;
  const markClass = side === "removed" ? "diff-word-del" : "diff-word-add";

  for (let i = 0; i < pairedCount; i++) {
    const oldLine = removedLines[i];
    const newLine = addedLines[i];
    const targetLine = side === "removed" ? oldLine : newLine;

    // Skip word-level diff if lines are too different
    if (similarity(oldLine, newLine) < 0.15) {
      outputLines.push(targetLine);
      continue;
    }

    // Table lines: diff cell-by-cell
    if (tableLineRe.test(oldLine) && tableLineRe.test(newLine)) {
      const oldCells = oldLine.split("|");
      const newCells = newLine.split("|");
      const resultCells: string[] = [];
      const cellCount = Math.max(oldCells.length, newCells.length);
      for (let c = 0; c < cellCount; c++) {
        const oc = c < oldCells.length ? oldCells[c] : "";
        const nc = c < newCells.length ? newCells[c] : "";
        if (oc === nc) {
          resultCells.push(side === "removed" ? oc : nc);
        } else {
          resultCells.push(diffCellContent(oc, nc, side, markClass));
        }
      }
      outputLines.push(resultCells.join("|"));
      continue;
    }

    // Image lines: skip word-level diff (breaks markdown parser)
    if (imageLineRe.test(oldLine) || imageLineRe.test(newLine)) {
      outputLines.push(targetLine);
      continue;
    }

    // Regular lines: word-level diff
    const oldTokens = tokenize(oldLine);
    const newTokens = tokenize(newLine);
    const diff = wordLcsDiff(oldTokens, newTokens);
    let line = "";
    for (const tok of diff) {
      if (tok.type === "equal") {
        line += tok.text;
      } else if (
        (side === "removed" && tok.type === "removed") ||
        (side === "added" && tok.type === "added")
      ) {
        if (tok.text.trim()) {
          line += `<span class="${markClass}">${tok.text}</span>`;
        } else {
          line += tok.text;
        }
      }
    }
    outputLines.push(line);
  }

  // Append unpaired lines
  if (side === "removed") {
    for (let i = pairedCount; i < removedLines.length; i++) outputLines.push(removedLines[i]);
  } else {
    for (let i = pairedCount; i < addedLines.length; i++) outputLines.push(addedLines[i]);
  }

  return outputLines.join("\n");
}

/** Diff content within a single table cell. */
function diffCellContent(
  oldCell: string,
  newCell: string,
  side: "removed" | "added",
  markClass: string,
): string {
  const oldToks = tokenize(oldCell);
  const newToks = tokenize(newCell);
  const diff = wordLcsDiff(oldToks, newToks);
  let cell = "";
  for (const tok of diff) {
    if (tok.type === "equal") {
      cell += tok.text;
    } else if (
      (side === "removed" && tok.type === "removed") ||
      (side === "added" && tok.type === "added")
    ) {
      if (tok.text.trim()) {
        cell += `<span class="${markClass}">${tok.text}</span>`;
      } else {
        cell += tok.text;
      }
    }
  }
  return cell;
}

/**
 * @deprecated Use buildRenderSegments + per-segment rendering instead.
 * Kept for backward compatibility.
 */
export function annotateMarkdown(
  _text: string,
  segments: DiffSegment[],
  side: "before" | "after",
): string {
  const outputLines: string[] = [];
  for (const segment of segments) {
    if (segment.type === "context") {
      outputLines.push(...segment.lines);
    } else if (segment.type === "removed" && side === "before") {
      outputLines.push(...segment.lines);
    } else if (segment.type === "added" && side === "after") {
      outputLines.push(...segment.lines);
    }
  }
  return outputLines.join("\n");
}
